import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Star } from "lucide-react";
import { Package, PackageOpen, PackageCheck, PackageX, QrCode, Search, Plus, Send, ArrowUpFromLine, ArrowDownToLine, Printer, MapPin, Clock, AlertTriangle, Ban, RefreshCw, Tag, ScanLine, FileSpreadsheet, Building2, Repeat, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import QRScannerDialog from "@/components/QRScannerDialog";
import * as XLSX from "xlsx";
import { useSortableTable, SortableTh } from "@/components/SortableTable";

const SAMPLE_TYPE_META = {
  buyer_original: { label: "Buyer's Original", color: "bg-violet-50 text-violet-800 border-violet-200" },
  pp_gold_seal: { label: "PP / Gold Seal", color: "bg-amber-50 text-amber-800 border-amber-200" },
  proto: { label: "Proto", color: "bg-sky-50 text-sky-800 border-sky-200" },
  size_set_silver_seal: { label: "Size Set / Silver Seal", color: "bg-slate-100 text-slate-800 border-slate-300" },
};
const STATUS_META = {
  in_stores: { label: "In Stores", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  issued: { label: "Issued", color: "bg-blue-50 text-blue-800 border-blue-200" },
  at_client_return: { label: "At Client · Return-back", color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  at_client_permanent: { label: "At Client · Permanent", color: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200" },
  lost: { label: "Lost", color: "bg-rose-50 text-rose-800 border-rose-200" },
};

function has(user, cap) {
  if (!user) return false;
  if (user.role === "admin" || user.role === "admin_addl") return true;
  return (user.capabilities || []).includes(cap);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function SampleGroupImages({ groupId, initial, canEdit }) {
  const [images, setImages] = useState(initial || []);
  const [lightbox, setLightbox] = useState(null); // {src, name} or null
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // {done, total}
  const [broken, setBroken] = useState(() => new Set()); // media ids whose file is missing on disk
  const refresh = async () => {
    try {
      const { data } = await api.get(`/samples/groups/${groupId}/media`);
      setImages(data.group_media || []);
      setBroken(new Set()); // re-probe after refresh
    } catch { /* silent */ }
  };
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    // Enforce the 5-image cap
    const remaining = 10 - images.length;
    if (remaining <= 0) { toast.error("Maximum 10 images per sample"); return; }
    const queue = files.slice(0, remaining);
    if (files.length > remaining) toast.warning(`Only ${remaining} of ${files.length} added — max 10 images per sample.`);
    // Validate size upfront
    const oversize = queue.find((f) => f.size > 15 * 1024 * 1024);
    if (oversize) { toast.error(`"${oversize.name}" exceeds 15 MB`); return; }
    setUploading(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < queue.length; i++) {
      const f = queue[i];
      setUploadProgress({ done: i, total: queue.length });
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/samples/groups/${groupId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (ex) {
        fail += 1;
        toast.error(`"${f.name}" — ${ex.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploadProgress(null);
    setUploading(false);
    if (ok > 0) toast.success(`Uploaded ${ok} image(s)${fail ? ` · ${fail} failed` : ""}`);
    refresh();
  };
  const remove = async (mid) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      await api.delete(`/samples/groups/${groupId}/media/${mid}`);
      toast.success("Removed");
      refresh();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Delete failed");
    }
  };
  const setCover = async (mid) => {
    try {
      await api.post(`/samples/groups/${groupId}/media/${mid}/set-cover`);
      toast.success("Cover updated");
      refresh();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to set cover");
    }
  };
  // Thumbnail URL for the small grid tile; full-res URL for the lightbox.
  const thumbSrc = (m) => `${API_BASE}/samples/groups/${groupId}/media/${m.id}?thumb=1`;
  const fullSrc = (m) => `${API_BASE}/samples/groups/${groupId}/media/${m.id}`;
  const markBroken = (mid) => setBroken((prev) => { const n = new Set(prev); n.add(mid); return n; });
  // Cover = explicit is_cover flag if any, else first uploaded (matches backend fallback).
  const explicitCoverIdx = images.findIndex((m) => m.is_cover === true);
  const coverIdx = explicitCoverIdx >= 0 ? explicitCoverIdx : (images.length > 0 ? 0 : -1);
  return (
    <div data-testid="sample-images-block">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sample images {images.length > 0 && <span className="text-slate-400 font-normal">({images.length}/10)</span>}</div>
        {canEdit && images.length < 10 && (
          <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:text-brand-dark cursor-pointer" data-testid="sample-images-add-more">
            <Plus className="h-3 w-3" /> Add {uploading && (uploadProgress ? `${uploadProgress.done + 1}/${uploadProgress.total}…` : "…")}
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={uploading} onChange={onPick} />
          </label>
        )}
      </div>
      {images.length === 0 ? (
        <div className="text-[12px] text-slate-400 italic px-2 py-2 border border-dashed rounded">No images uploaded for this sample yet.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {images.map((m, i) => {
            const isBroken = broken.has(m.id);
            return (
              <div key={m.id} className={`relative w-24 h-24 rounded-md overflow-hidden border ${i === coverIdx ? "border-brand ring-1 ring-brand/40" : "border-slate-200"} ${isBroken ? "bg-rose-50" : "bg-slate-50"} group cursor-pointer`} onClick={() => !isBroken && setLightbox({ src: fullSrc(m), name: m.filename, mid: m.id })} data-testid={`sample-image-${i}`}>
                {isBroken ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-rose-600 gap-1 px-1 text-center" data-testid={`sample-image-broken-${i}`}>
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-[9px] font-semibold uppercase tracking-wide">Missing file</span>
                    <span className="text-[8.5px] text-rose-500 truncate w-full" title={m.filename}>{m.filename}</span>
                  </div>
                ) : (
                  <img
                    src={thumbSrc(m)}
                    alt={m.filename}
                    className="object-cover w-full h-full"
                    loading="lazy"
                    onError={(e) => {
                      // First try full-size (thumb generation may be broken); mark as broken only on second failure.
                      if (!e.currentTarget.dataset.retried) {
                        e.currentTarget.dataset.retried = "1";
                        e.currentTarget.src = fullSrc(m);
                      } else {
                        markBroken(m.id);
                      }
                    }}
                  />
                )}
                {!isBroken && i === coverIdx && <span className="absolute bottom-0 left-0 text-[9px] font-semibold px-1 py-0.5 bg-brand text-white uppercase tracking-wide">Cover</span>}
                {canEdit && !isBroken && i !== coverIdx && (
                  <button type="button" title="Set as cover" onClick={(e) => { e.stopPropagation(); setCover(m.id); }} className="absolute bottom-0 left-0 px-1 py-0.5 text-[9px] font-semibold bg-white/90 text-slate-700 hover:bg-brand hover:text-white opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-0.5" data-testid={`sample-image-set-cover-${i}`}>
                    <Star className="h-2.5 w-2.5" /> Cover
                  </button>
                )}
                {canEdit && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); remove(m.id); }} className={`absolute top-0 right-0 p-0.5 text-white transition-opacity ${isBroken ? "bg-rose-600 opacity-100" : "bg-black/60 opacity-0 group-hover:opacity-100"}`} data-testid={`sample-image-remove-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="sample-image-lightbox">
          <button type="button" className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/40 text-white" onClick={() => setLightbox(null)}><X className="h-5 w-5" /></button>
          <img src={lightbox.src} alt={lightbox.name} className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

export default function SampleRegister() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [samples, setSamples] = useState([]);
  const [requests, setRequests] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reqStatusFilter, setReqStatusFilter] = useState("open");
  const [recycleBin, setRecycleBin] = useState([]);
  const [recycleLoading, setRecycleLoading] = useState(false);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    sample_type: "buyer_original", buyer_name: "", buyer_style: "", bbs_style: "", date_of_receipt_or_make: todayIso(), no_of_pcs: 1,
    sizes: [""],
  });
  const [registerImages, setRegisterImages] = useState([]); // File[] queued for upload after registration
  const [registering, setRegistering] = useState(false);
  const [dupCheck, setDupCheck] = useState(null); // { group, form } when duplicate is detected

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestTarget, setRequestTarget] = useState(null);
  const [requestForm, setRequestForm] = useState({
    expected_return_date: "", reason: "",
  });

  // Dispatch-to-client (merchandiser / admin) — populates courier + waybill after Stores has issued
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchTarget, setDispatchTarget] = useState(null); // the open sample_request row (single-mode)
  const [dispatchMode, setDispatchMode] = useState("single"); // "single" | "bulk"
  const [bulkTargets, setBulkTargets] = useState([]);          // array of request rows (bulk-mode)
  const [selectedReqIds, setSelectedReqIds] = useState(new Set()); // requests-tab multi-select
  const [dispatchForm, setDispatchForm] = useState({
    client_name: "", courier: "", waybill: "",
    client_mode: "return_back", expected_return_date: "", note: "",
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSample, setDetailSample] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [batchPrintOpen, setBatchPrintOpen] = useState(false);

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueTarget, setIssueTarget] = useState(null);
  const [issueForm, setIssueForm] = useState({ to_location: "", note: "" });

  const canRegister = has(user, "register_sample");
  const canRequest = has(user, "request_sample");
  const canIssue = has(user, "issue_receive_sample");
  const canEditBbs = has(user, "edit_bbs_style_after_reg");
  const canMarkLost = has(user, "mark_sample_lost");
  const canDeleteSample = has(user, "delete_sample");
  const canFlipClient = has(user, "flip_client_mode");
  const canDispatch = has(user, "dispatch_sample_to_client");
  const canSeeCourier = has(user, "view_courier_waybill");

  const loadDash = async () => {
    try {
      const { data } = await api.get("/samples/dashboard");
      setDash(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load dashboard"); }
  };
  const loadSamples = async () => {
    try {
      const params = {};
      if (typeFilter !== "all") params.sample_type = typeFilter;
      if (statusFilter !== "all") params.status_filter = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/samples", { params });
      setSamples(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load samples"); }
  };
  const loadRequests = async () => {
    try {
      const params = {};
      // "open", "overdue", "in_circulation" are filtered client-side (see filteredRequests),
      // everything else is a real request.status value the API understands.
      const clientSideOnly = new Set(["open", "overdue", "in_circulation"]);
      if (!clientSideOnly.has(reqStatusFilter) && reqStatusFilter !== "all") {
        params.status_filter = reqStatusFilter;
      }
      const { data } = await api.get("/samples/requests", { params });
      setRequests(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load requests"); }
  };
  const loadRecycleBin = async () => {
    setRecycleLoading(true);
    try {
      const { data } = await api.get("/samples/recycle-bin");
      setRecycleBin(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Recycle Bin");
    } finally {
      setRecycleLoading(false);
    }
  };
  const restoreFromBin = async (row) => {
    if (!row?.sample_group_id) return;
    if (!window.confirm(`Restore this sample group?\n\n${row.buyer_name} · ${row.buyer_style}${row.bbs_style ? " / " + row.bbs_style : ""}\n${row.pieces_in_group} piece(s) will reappear in the live Sample Register.`)) return;
    try {
      const { data } = await api.post(`/samples/recycle-bin/${row.sample_group_id}/restore`);
      toast.success(`Restored — ${data.pieces_restored} piece(s), ${data.transactions_restored} txn(s), ${data.media_restored} image(s).`);
      loadRecycleBin();
      loadDash();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };
  const purgeFromBin = async (row) => {
    if (!row?.sample_group_id) return;
    const confirmed = window.prompt(
      `PERMANENT DELETE — cannot be undone.\n\n${row.buyer_name} · ${row.buyer_style}${row.bbs_style ? " / " + row.bbs_style : ""}\n${row.pieces_in_group} piece(s) will be dropped forever.\n\nType PURGE to confirm.`,
      ""
    );
    if (confirmed === null) return;
    if (String(confirmed).trim().toUpperCase() !== "PURGE") {
      toast.error('You did not type "PURGE" — aborted.');
      return;
    }
    try {
      const { data } = await api.delete(`/samples/recycle-bin/${row.sample_group_id}/purge`);
      toast.success(`Purged — ${data.pieces_deleted} piece(s), ${data.transactions_deleted} txn(s), ${data.media_deleted} image(s).`);
      loadRecycleBin();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
  };
  const loadBuyers = async () => {
    try {
      const { data } = await api.get("/masters/buyers");
      setBuyers(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadDash(); loadBuyers(); }, []);
  useEffect(() => { if (tab === "samples") loadSamples(); }, [tab, typeFilter, statusFilter]);
  useEffect(() => { if (tab === "recycle-bin") loadRecycleBin(); }, [tab]);
  // Debounced live-search — reload the samples list ~300ms after the user stops typing.
  // Empty search reloads immediately so clearing the box shows all rows again.
  useEffect(() => {
    if (tab !== "samples") return;
    const t = setTimeout(() => { loadSamples(); }, search.trim() === "" ? 0 : 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tab]);
  useEffect(() => { if (tab === "requests") loadRequests(); }, [tab, reqStatusFilter]);
  // Clear the multi-select whenever the requests filter/tab changes so a hidden
  // ticked row can't sneak into a bulk-dispatch (the eligibility check would
  // catch it, but the UX confusion "why is my count wrong?" is worse).
  useEffect(() => { setSelectedReqIds(new Set()); }, [reqStatusFilter, tab]);

  const filteredRequests = useMemo(() => {
    if (reqStatusFilter === "open") return requests.filter((r) => r.status === "pending" || r.status === "issued");
    if (reqStatusFilter === "overdue") return requests.filter((r) => r.is_overdue);
    if (reqStatusFilter === "in_circulation") {
      return requests.filter((r) => r.status === "issued" && r.sample?.status !== "at_client_permanent");
    }
    return requests;
  }, [requests, reqStatusFilter]);

  // Dashboard click-through helpers — jump into the appropriate tab with filters pre-set.
  const jumpToSamples = (type = "all", status = "all") => {
    setTypeFilter(type);
    setStatusFilter(status);
    setTab("samples");
  };
  const jumpToRequests = (reqStatus = "open") => {
    setReqStatusFilter(reqStatus);
    setTab("requests");
  };

  const doRegister = async (payload, { silentToastCount } = {}) => {
    setRegistering(true);
    try {
      const { data } = await api.post("/samples", payload);
      const appended = data.appended;
      const total = data.group_total;
      const added = data.count;
      toast.success(
        appended
          ? `Added ${added} piece(s) to existing group — group is now ${total} pcs (SEQ /${total})`
          : `Registered ${added} piece(s) — group created`
      );
      // Upload queued images (best-effort — if any fails, we still consider registration successful)
      if (registerImages.length > 0) {
        let uploaded = 0;
        for (const img of registerImages) {
          try {
            const fd = new FormData();
            fd.append("file", img);
            await api.post(`/samples/groups/${data.sample_group_id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            uploaded += 1;
          } catch (ex) {
            toast.error(ex.response?.data?.detail || `Failed to upload ${img.name}`);
          }
        }
        if (uploaded > 0) toast.success(`Uploaded ${uploaded} image(s)`);
      }
      setRegisterOpen(false);
      setDupCheck(null);
      setRegisterForm({ sample_type: "buyer_original", buyer_name: "", buyer_style: "", bbs_style: "", date_of_receipt_or_make: todayIso(), no_of_pcs: 1, sizes: [""] });
      setRegisterImages([]);
      // Auto-open the tag sheet in a new tab so Stores can print immediately.
      // When appending, we print ONLY the freshly added pieces (via ids=) — not the whole group.
      const url = appended
        ? `${API_BASE}/samples/tags.pdf?ids=${(data.samples || []).map((s) => s.id).join(",")}`
        : `${API_BASE}/samples/tags.pdf?group=${data.sample_group_id}`;
      window.open(url, "_blank", "noopener");
      loadDash();
      if (tab === "samples") loadSamples();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    }
    setRegistering(false);
  };

  const submitRegister = async () => {
    const f = registerForm;
    if (!f.buyer_name || !f.buyer_style || !f.date_of_receipt_or_make) {
      toast.error("Buyer, Buyer Style and Date are required");
      return;
    }
    if (f.no_of_pcs < 1 || f.no_of_pcs > 50) { toast.error("Number of pieces must be 1–50"); return; }
    // Step 1 — see if a group with the same (type, buyer, style, date) already exists
    setRegistering(true);
    try {
      const { data: dup } = await api.get("/samples/check-duplicate", {
        params: {
          sample_type: f.sample_type,
          buyer_name: f.buyer_name,
          buyer_style: f.buyer_style,
          date_of_receipt_or_make: f.date_of_receipt_or_make,
        },
      });
      if (dup?.exists) {
        // Pause and let the user decide append vs new group
        setDupCheck({ group: dup.group, form: { ...f, no_of_pcs: Number(f.no_of_pcs) } });
        setRegistering(false);
        return;
      }
    } catch (e) {
      // Non-fatal: fall through to plain register
    }
    setRegistering(false);
    const cleanedSizes = (f.sizes || []).slice(0, Number(f.no_of_pcs)).map((s) => String(s || "").trim());
    await doRegister({ ...f, no_of_pcs: Number(f.no_of_pcs), sizes: cleanedSizes });
  };

  const openRequest = (sample) => {
    setRequestTarget(sample);
    const d = new Date(); d.setDate(d.getDate() + 3);
    setRequestForm({
      expected_return_date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      reason: "",
    });
    setRequestOpen(true);
  };
  const submitRequest = async () => {
    const f = requestForm;
    if (!f.reason.trim()) { toast.error("Reason is required"); return; }
    if (!f.expected_return_date) { toast.error("Expected return date is required"); return; }
    try {
      await api.post("/samples/requests", {
        sample_id: requestTarget.id,
        expected_return_date: f.expected_return_date,
        reason: f.reason.trim(),
      });
      toast.success("Request raised — Stores will be notified");
      setRequestOpen(false);
      loadDash();
      if (tab === "samples") loadSamples();
    } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
  };

  const openDispatch = (req) => {
    if (!req) return;
    const d = new Date(); d.setDate(d.getDate() + 21);
    setDispatchMode("single");
    setDispatchTarget(req);
    setBulkTargets([]);
    setDispatchForm({
      client_name: "",
      courier: "",
      waybill: "",
      client_mode: "return_back",
      expected_return_date: req.expected_return_date || `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      note: "",
    });
    setDispatchOpen(true);
  };
  const openBulkDispatch = () => {
    const rows = requests.filter((r) => selectedReqIds.has(r.id));
    if (!rows.length) { toast.error("Select at least one eligible request"); return; }
    // Server enforces eligibility too, but keep the client honest.
    const ineligible = rows.filter((r) => r.status !== "issued" || r.sample?.status !== "issued" || r.client_dispatched_at);
    if (ineligible.length) { toast.error(`${ineligible.length} selected row(s) not eligible for client dispatch`); return; }
    const d = new Date(); d.setDate(d.getDate() + 21);
    setDispatchMode("bulk");
    setDispatchTarget(null);
    setBulkTargets(rows);
    setDispatchForm({
      client_name: "",
      courier: "",
      waybill: "",
      client_mode: "return_back",
      expected_return_date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      note: "",
    });
    setDispatchOpen(true);
  };
  const submitDispatch = async () => {
    const f = dispatchForm;
    if (!f.client_name.trim()) { toast.error("Client name is required"); return; }
    if (!f.courier.trim()) { toast.error("Courier is required"); return; }
    if (!f.waybill.trim()) { toast.error("Waybill / tracking # is required"); return; }
    if (f.client_mode === "return_back" && !f.expected_return_date) {
      toast.error("Expected return date is required for Return-back mode"); return;
    }
    try {
      if (dispatchMode === "bulk") {
        const res = await api.post(`/samples/requests/dispatch-to-client-bulk`, {
          request_ids: bulkTargets.map((r) => r.id),
          client_name: f.client_name.trim(),
          courier: f.courier.trim(),
          waybill: f.waybill.trim(),
          client_mode: f.client_mode,
          expected_return_date: f.client_mode === "return_back" ? f.expected_return_date : "",
          note: f.note.trim(),
        });
        toast.success(`Dispatched ${res.data?.count || bulkTargets.length} samples to ${f.client_name.trim()} — ${f.courier.trim()} (${f.waybill.trim()})`);
        setSelectedReqIds(new Set());
      } else {
        await api.post(`/samples/requests/${dispatchTarget.id}/dispatch-to-client`, {
          client_name: f.client_name.trim(),
          courier: f.courier.trim(),
          waybill: f.waybill.trim(),
          client_mode: f.client_mode,
          expected_return_date: f.client_mode === "return_back" ? f.expected_return_date : "",
          note: f.note.trim(),
        });
        toast.success(`Dispatched to ${f.client_name.trim()} — ${f.courier.trim()} (${f.waybill.trim()})`);
      }
      setDispatchOpen(false);
      setDispatchTarget(null);
      setBulkTargets([]);
      setDispatchMode("single");
      loadDash();
      if (tab === "samples") loadSamples();
      if (tab === "requests") loadRequests();
      if (detailOpen && detailSample) openSampleDetail(detailSample.sample);
    } catch (e) { toast.error(e.response?.data?.detail || "Dispatch failed"); }
  };

  const flipClientMode = async (sample) => {
    const goingToPermanent = sample.status === "at_client_return";
    const target_mode = goingToPermanent ? "permanent" : "return_back";
    let exp = "";
    if (target_mode === "return_back") {
      exp = window.prompt("Expected return date (YYYY-MM-DD):", "");
      if (!exp) return;
    } else {
      if (!window.confirm(`Change to PERMANENT? Overdue notifications will stop and the sample will no longer be expected back.`)) return;
    }
    try {
      await api.post(`/samples/${sample.id}/flip-client-mode`, { mode: target_mode, expected_return_date: exp });
      toast.success(`Mode changed to ${target_mode === "permanent" ? "Permanent" : "Return-back"}`);
      loadDash();
      if (tab === "samples") loadSamples();
      if (tab === "requests") loadRequests();
      if (detailOpen) openSampleDetail(sample);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openIssue = (req) => {
    setIssueTarget(req);
    const default_to = req.requested_by_name || req.requested_by || "";
    setIssueForm({ to_location: default_to, note: "" });
    setIssueOpen(true);
  };
  const submitIssue = async () => {
    if (!issueForm.to_location.trim()) { toast.error("Destination is required"); return; }
    try {
      await api.post(`/samples/requests/${issueTarget.id}/issue`, issueForm);
      toast.success("Issued");
      setIssueOpen(false);
      loadRequests();
      loadDash();
    } catch (e) { toast.error(e.response?.data?.detail || "Issue failed"); }
  };
  const receiveRequest = async (req) => {
    if (!window.confirm(`Receive back "${req.sample?.buyer_style} · ${req.sample?.seq_label}" to Stores?`)) return;
    try {
      await api.post(`/samples/requests/${req.id}/receive`, {});
      toast.success("Received back to Stores");
      loadRequests();
      loadDash();
    } catch (e) { toast.error(e.response?.data?.detail || "Receive failed"); }
  };
  const cancelRequest = async (req) => {
    if (!window.confirm("Cancel this pending request?")) return;
    try {
      await api.post(`/samples/requests/${req.id}/cancel`, {});
      toast.success("Cancelled");
      loadRequests();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const openSampleDetail = async (s) => {
    try {
      const { data } = await api.get(`/samples/${s.id}`);
      setDetailSample(data);
      setDetailOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };

  const markLost = async (sample) => {
    const note = window.prompt("Reason / note for marking this sample as lost:", "");
    if (note === null) return;
    try {
      await api.post(`/samples/${sample.id}/mark-lost`, { note });
      toast.success("Marked as lost");
      loadDash();
      if (tab === "samples") loadSamples();
      if (detailOpen) openSampleDetail(sample);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteWholeSample = async (detail) => {
    const s = detail?.sample;
    if (!s) return;
    const totalPcs = (detail.group || []).length || s.no_of_pcs || 1;
    const txCount = (detail.transactions || []).length;
    const mediaCount = (detail.group_media || []).length;
    const line1 = `About to move this sample to the Recycle Bin:`;
    const line2 = `• ${totalPcs} piece(s) — ${s.buyer_name} · ${s.buyer_style}${s.bbs_style ? " / " + s.bbs_style : ""}`;
    const line3 = `• ${txCount} transaction(s), any linked requests, and ${mediaCount} image(s) will be moved together.`;
    const line4 = `You can restore it from Sample Register → Recycle Bin within 30 days; after that it is purged automatically.`;
    const confirmed = window.prompt(
      `${line1}\n${line2}\n${line3}\n\n${line4}\n\nType DELETE to confirm.`,
      ""
    );
    if (confirmed === null) return;
    if (String(confirmed).trim().toUpperCase() !== "DELETE") {
      toast.error('You did not type "DELETE" — aborted.');
      return;
    }
    try {
      const { data } = await api.delete(`/samples/groups/${s.sample_group_id}`);
      toast.success(`Moved to Recycle Bin — ${data.pieces_deleted} piece(s), ${data.transactions_deleted} txn(s), ${data.media_deleted} image(s). Restorable for 30 days.`);
      setDetailOpen(false);
      setDetailSample(null);
      loadDash();
      if (tab === "samples") loadSamples();
      if (tab === "recycle-bin") loadRecycleBin();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const updateBbsStyle = async (sample) => {
    const value = window.prompt("Update BBS Style # (blank = clear):", sample.bbs_style || "");
    if (value === null) return;
    try {
      await api.patch(`/samples/${sample.id}`, { bbs_style: value });
      toast.success("BBS Style updated for the whole group");
      loadDash();
      if (tab === "samples") loadSamples();
      if (detailOpen) openSampleDetail(sample);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const printGroupTags = (sample) => {
    if (!sample?.sample_group_id) return;
    const url = `${API_BASE}/samples/tags.pdf?group=${sample.sample_group_id}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="space-y-6" data-testid="sample-register-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Package className="h-6 w-6 text-brand" /> Sample Register
          </h1>
          <p className="text-[13px] text-slate-500 mt-0.5">Buyer originals & in-house developed samples (PP/Gold, Proto, Size Set/Silver) — with QR tags, issue/receive and overdue alerts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setScannerOpen(true)} data-testid="samples-scan-btn">
            <ScanLine className="h-4 w-4 mr-1.5" /> Scan tag
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBatchPrintOpen(true)} data-testid="samples-batch-print-btn">
            <Printer className="h-4 w-4 mr-1.5" /> Print QR batch
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadDash(); if (tab === "samples") loadSamples(); if (tab === "requests") loadRequests(); }} data-testid="samples-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          {canRegister && (
            <Button className="bg-brand hover:bg-brand-dark text-white" onClick={() => setRegisterOpen(true)} data-testid="register-sample-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Register sample
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`grid w-full sm:w-auto sm:inline-grid ${canDeleteSample ? "grid-cols-5" : "grid-cols-4"}`}>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><PackageOpen className="h-3.5 w-3.5 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="samples" data-testid="tab-samples"><Tag className="h-3.5 w-3.5 mr-1.5" /> Samples</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests"><Send className="h-3.5 w-3.5 mr-1.5" /> Requests</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report"><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Report</TabsTrigger>
          {canDeleteSample && (
            <TabsTrigger value="recycle-bin" data-testid="tab-recycle-bin"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Recycle Bin</TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="mt-6 space-y-6">
          {!dash ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3">
                {[
                  { k: "buyer_originals", label: "Buyer Originals", icon: PackageOpen, color: "text-violet-700",
                    onClick: () => jumpToSamples("buyer_original", "all") },
                  { k: "in_house_developed", label: "In-House Counter Samples", icon: PackageCheck, color: "text-amber-700",
                    sub: [
                      `PP/Gold: ${dash.totals.pp_gold_seals ?? 0}`,
                      `Proto: ${dash.totals.protos ?? 0}`,
                      `Size Set/Silver: ${dash.totals.size_set_silver_seals ?? 0}`,
                    ],
                    onClick: () => jumpToSamples("in_house", "all") },
                  { k: "in_stores", label: "In Stores", icon: Package, color: "text-emerald-700",
                    onClick: () => jumpToSamples("all", "in_stores") },
                  { k: "issued", label: "Issued Out", icon: ArrowUpFromLine, color: "text-blue-700",
                    onClick: () => jumpToSamples("all", "any_out") },
                  { k: "overdue", label: "Overdue", icon: AlertTriangle, color: "text-rose-700",
                    onClick: () => jumpToRequests("overdue") },
                  { k: "awaiting_issue", label: "Awaiting Issue", icon: Send, color: "text-amber-700",
                    sub: "Requests pending Stores action",
                    onClick: () => jumpToRequests("pending") },
                  { k: "in_circulation", label: "In Circulation", icon: Repeat, color: "text-indigo-700",
                    sub: "Samples currently in someone's hands",
                    onClick: () => jumpToRequests("in_circulation") },
                  { k: "lost", label: "Lost", icon: PackageX, color: "text-slate-700",
                    onClick: () => jumpToSamples("all", "lost") },
                ].map((kpi) => (
                  <Card
                    key={kpi.k}
                    className="p-4 cursor-pointer transition-shadow hover:shadow-md hover:border-brand/40"
                    data-testid={`kpi-${kpi.k}`}
                    role="button"
                    tabIndex={0}
                    onClick={kpi.onClick}
                    onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && kpi.onClick) { e.preventDefault(); kpi.onClick(); } }}
                  >
                    <div className="flex items-center gap-2">
                      <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{kpi.label}</div>
                    </div>
                    <div className="text-2xl font-heading font-bold mt-1">{dash.totals[kpi.k] ?? 0}</div>
                    {kpi.sub && (
                      Array.isArray(kpi.sub) ? (
                        <div className="text-[10.5px] text-slate-500 font-mono mt-0.5 leading-snug space-y-0.5">
                          {kpi.sub.map((line, i) => (<div key={i}>{line}</div>))}
                        </div>
                      ) : (
                        <div className="text-[10.5px] text-slate-500 font-mono mt-0.5 leading-snug">{kpi.sub}</div>
                      )
                    )}
                  </Card>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-brand" />
                    <div className="font-heading font-semibold text-sm">Issued-out by location</div>
                  </div>
                  {dash.by_location.length === 0 ? (
                    <div className="text-[13px] text-slate-500">Nothing issued out currently.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {dash.by_location.map((row) => (
                        <div key={row.location} className="flex items-center justify-between text-[13px] px-2 py-1.5 rounded hover:bg-slate-50 dark:hover:bg-white/5">
                          <span className="truncate">{row.location}</span>
                          <Badge variant="secondary">{row.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-brand" />
                    <div className="font-heading font-semibold text-sm">Recent transactions</div>
                  </div>
                  {dash.recent_transactions.length === 0 ? (
                    <div className="text-[13px] text-slate-500">No transactions yet.</div>
                  ) : (
                    <div className="space-y-1 max-h-72 overflow-y-auto">
                      {dash.recent_transactions.map((t) => (
                        <div key={t.id} className="text-[12px] px-2 py-1.5 rounded hover:bg-slate-50 dark:hover:bg-white/5 flex items-start gap-2">
                          <Badge variant="outline" className="mt-0.5 shrink-0 capitalize">{t.action}</Badge>
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{t.note || `${t.from_location || "—"} → ${t.to_location || "—"}`}</div>
                            <div className="text-slate-400 text-[10.5px] mt-0.5">{fmtDateTime(t.at)} · {t.actor}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Samples list */}
        <TabsContent value="samples" className="mt-6 space-y-4">
          <Card className="p-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search buyer, style, description, QR id…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadSamples()} className="pl-9" data-testid="samples-search" />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[220px]" data-testid="samples-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="buyer_original">Buyer's Original</SelectItem>
                  <SelectItem value="in_house">In-House (PP/Gold + Proto + Size Set)</SelectItem>
                  <SelectItem value="pp_gold_seal">PP / Gold Seal</SelectItem>
                  <SelectItem value="proto">Proto</SelectItem>
                  <SelectItem value="size_set_silver_seal">Size Set / Silver Seal</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]" data-testid="samples-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="in_stores">In Stores</SelectItem>
                  <SelectItem value="any_out">Issued Out (anywhere)</SelectItem>
                  <SelectItem value="issued">Issued (internal)</SelectItem>
                  <SelectItem value="at_client_return">At Client · Return-back</SelectItem>
                  <SelectItem value="at_client_permanent">At Client · Permanent</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadSamples} data-testid="samples-apply-filter">Apply</Button>
            </div>
          </Card>

          {samples.length === 0 ? (
            <Card className="p-8 text-center text-slate-500 text-sm">No samples match your filters.</Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {samples.map((s) => (
                <Card key={s.id} className="p-3 hover:shadow-md transition-shadow" data-testid={`sample-card-${s.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="shrink-0">
                      {s.cover_media_id ? (
                        <img
                          src={`${API_BASE}/samples/groups/${s.sample_group_id}/media/${s.cover_media_id}?thumb=1`}
                          alt="Cover"
                          className="h-16 w-16 border rounded object-cover bg-slate-50"
                          loading="lazy"
                          onError={(e) => {
                            // Retry with full-size first, then fall back to QR on second failure.
                            if (!e.currentTarget.dataset.retried) {
                              e.currentTarget.dataset.retried = "1";
                              e.currentTarget.src = `${API_BASE}/samples/groups/${s.sample_group_id}/media/${s.cover_media_id}`;
                            } else {
                              e.currentTarget.src = `${API_BASE}/samples/${s.id}/qr.png`;
                              e.currentTarget.className = "h-16 w-16 border rounded";
                            }
                          }}
                          data-testid={`sample-cover-${s.id}`}
                        />
                      ) : (
                        <img src={`${API_BASE}/samples/${s.id}/qr.png`} alt="QR" className="h-16 w-16 border rounded" data-testid={`sample-qr-${s.id}`} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${SAMPLE_TYPE_META[s.sample_type]?.color || ""}`}>{SAMPLE_TYPE_META[s.sample_type]?.label || s.sample_type}</span>
                        <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_META[s.status]?.color || ""}`}>{STATUS_META[s.status]?.label || s.status}</span>
                        <span className="text-[10.5px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{s.seq_label} / {s.no_of_pcs}</span>
                      </div>
                      <div className="text-[13.5px] font-semibold text-slate-900 dark:text-slate-100 truncate">{s.buyer_name} · {s.buyer_style}</div>
                      <div className="text-[12px] text-slate-500 truncate">BBS#: {s.bbs_style || "—"} · QR {s.qr_id}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Registered {fmtDate(s.registration_date)} · at {s.current_location || "Stores"}</div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Button size="sm" variant="outline" onClick={() => openSampleDetail(s)} data-testid={`view-sample-${s.id}`}>View</Button>
                        {canRequest && s.status === "in_stores" && (
                          <Button size="sm" variant="outline" onClick={() => openRequest(s)} data-testid={`request-sample-${s.id}`}><Send className="h-3.5 w-3.5 mr-1" />Request</Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => printGroupTags(s)} data-testid={`print-sample-${s.id}`}><Printer className="h-3.5 w-3.5 mr-1" />Print tags</Button>
                        {canEditBbs && (
                          <Button size="sm" variant="ghost" onClick={() => updateBbsStyle(s)}>Edit BBS#</Button>
                        )}
                        {canMarkLost && s.status !== "lost" && (
                          <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => markLost(s)}><Ban className="h-3.5 w-3.5 mr-1" />Lost</Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Requests */}
        <TabsContent value="requests" className="mt-6 space-y-4">
          <Card className="p-3">
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={reqStatusFilter} onValueChange={setReqStatusFilter}>
                <SelectTrigger className="w-[260px]" data-testid="request-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open (pending + issued)</SelectItem>
                  <SelectItem value="pending">Awaiting Issue (pending only)</SelectItem>
                  <SelectItem value="in_circulation">In Circulation</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="issued">Issued only</SelectItem>
                  <SelectItem value="received">Received (closed)</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadRequests} data-testid="requests-refresh">Refresh</Button>
            </div>
          </Card>

          {/* Bulk-dispatch selection toolbar — shows when any eligible row is ticked.
              Same-client + same-AWB is enforced in the dialog (user confirmed defaults).
              Only visible to users with dispatch_sample_to_client capability. */}
          {canDispatch && (() => {
            const eligibleRows = filteredRequests.filter((r) => r.status === "issued" && r.sample?.status === "issued" && !r.client_dispatched_at);
            const selectedCount = eligibleRows.filter((r) => selectedReqIds.has(r.id)).length;
            if (eligibleRows.length === 0) return null;
            const allSelected = selectedCount > 0 && selectedCount === eligibleRows.length;
            const toggleAll = () => {
              setSelectedReqIds((prev) => {
                const next = new Set(prev);
                if (allSelected) {
                  eligibleRows.forEach((r) => next.delete(r.id));
                } else {
                  eligibleRows.forEach((r) => next.add(r.id));
                }
                return next;
              });
            };
            return (
              <Card className="p-2.5 border-indigo-200 bg-indigo-50/60 flex flex-wrap items-center gap-3" data-testid="bulk-dispatch-toolbar">
                <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = selectedCount > 0 && !allSelected; }}
                    onChange={toggleAll}
                    className="h-4 w-4 accent-indigo-600"
                    data-testid="bulk-dispatch-select-all"
                  />
                  <span className="font-semibold text-slate-700">
                    {selectedCount > 0 ? `${selectedCount} of ${eligibleRows.length} selected` : `Select all ${eligibleRows.length} eligible for client dispatch`}
                  </span>
                </label>
                <div className="ml-auto flex items-center gap-2">
                  {selectedCount > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedReqIds(new Set())}
                      data-testid="bulk-dispatch-clear"
                    >Clear</Button>
                  )}
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    disabled={selectedCount === 0}
                    onClick={openBulkDispatch}
                    data-testid="bulk-dispatch-open"
                  >
                    <Building2 className="h-3.5 w-3.5 mr-1" />Dispatch {selectedCount || ""} to client
                  </Button>
                </div>
              </Card>
            );
          })()}

          {filteredRequests.length === 0 ? (
            <Card className="p-8 text-center text-slate-500 text-sm">No requests to show.</Card>
          ) : (
            <div className="space-y-2">
              {filteredRequests.map((r) => {
                const isEligibleForBulk = r.status === "issued" && r.sample?.status === "issued" && !r.client_dispatched_at;
                const isSelected = selectedReqIds.has(r.id);
                return (
                <Card key={r.id} className={`p-3 ${r.is_overdue ? "border-rose-300 bg-rose-50/40" : ""} ${isSelected ? "ring-2 ring-indigo-400 bg-indigo-50/30" : ""}`} data-testid={`request-card-${r.id}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    {canDispatch && isEligibleForBulk && (
                      <label className="flex items-center pt-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedReqIds((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(r.id); else next.delete(r.id);
                              return next;
                            });
                          }}
                          className="h-4 w-4 accent-indigo-600"
                          data-testid={`bulk-dispatch-check-${r.id}`}
                          aria-label={`Select ${r.sample?.buyer_style} · ${r.sample?.seq_label} for bulk dispatch`}
                        />
                      </label>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${SAMPLE_TYPE_META[r.sample?.sample_type]?.color || ""}`}>{SAMPLE_TYPE_META[r.sample?.sample_type]?.label || r.sample?.sample_type}</span>
                        <Badge variant={r.status === "pending" ? "secondary" : r.status === "issued" ? "default" : r.status === "received" ? "outline" : "secondary"} className="capitalize">{r.status}</Badge>
                        {r.target === "client" && (
                          <Badge className={r.client_mode === "permanent" ? "bg-fuchsia-600 hover:bg-fuchsia-700" : "bg-indigo-600 hover:bg-indigo-700"}>
                            <Building2 className="h-3 w-3 mr-1" />Client · {r.client_mode === "permanent" ? "Permanent" : "Return-back"}
                          </Badge>
                        )}
                        {r.is_overdue && (
                          <Badge className="bg-rose-600 hover:bg-rose-700"><AlertTriangle className="h-3 w-3 mr-1" />Overdue</Badge>
                        )}
                      </div>
                      <div className="text-[13.5px] font-semibold truncate">{r.sample?.buyer_name} · {r.sample?.buyer_style} · {r.sample?.seq_label}</div>
                      <div className="text-[12px] text-slate-500">BBS#: {r.sample?.bbs_style || "—"} · Requested by {r.requested_by_name}</div>
                      {r.target === "client" && (
                        <div className="text-[12px] text-slate-600">
                          Client: <b>{r.client_name}</b>
                          {canSeeCourier && r.courier ? ` · ${r.courier}` : ""}
                          {canSeeCourier && r.waybill ? ` · ${r.waybill}` : ""}
                          {!canSeeCourier && (r.courier || r.waybill) && <span className="ml-1 text-slate-400 italic">(courier/waybill hidden)</span>}
                        </div>
                      )}
                      <div className="text-[12px] text-slate-500">Reason: {r.reason}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Raised {fmtDateTime(r.created_at)}
                        {r.expected_return_date && ` · Expected back ${fmtDate(r.expected_return_date)}`}
                        {r.target === "client" && r.client_mode === "permanent" && ` · No return expected`}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {canIssue && r.status === "pending" && (
                        <Button size="sm" className="bg-brand text-white hover:bg-brand-dark" onClick={() => openIssue(r)} data-testid={`issue-request-${r.id}`}>
                          <ArrowUpFromLine className="h-3.5 w-3.5 mr-1" />Issue
                        </Button>
                      )}
                      {canDispatch && r.status === "issued" && r.sample?.status === "issued" && !r.client_dispatched_at && (
                        <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => openDispatch(r)} data-testid={`dispatch-request-${r.id}`}>
                          <Building2 className="h-3.5 w-3.5 mr-1" />Dispatch to client
                        </Button>
                      )}
                      {canIssue && r.status === "issued" && r.sample?.status !== "at_client_permanent" && (
                        <Button size="sm" variant="outline" onClick={() => receiveRequest(r)} data-testid={`receive-request-${r.id}`}>
                          <ArrowDownToLine className="h-3.5 w-3.5 mr-1" />Receive
                        </Button>
                      )}
                      {canFlipClient && r.status === "issued" && (r.sample?.status === "at_client_return" || r.sample?.status === "at_client_permanent") && (
                        <Button size="sm" variant="outline" onClick={() => flipClientMode({ id: r.sample_id, status: r.sample?.status })} data-testid={`flip-mode-${r.id}`}>
                          <Repeat className="h-3.5 w-3.5 mr-1" />Change to {r.sample?.status === "at_client_permanent" ? "Return-back" : "Permanent"}
                        </Button>
                      )}
                      {r.status === "pending" && (r.requested_by === user?.email || (user?.role === "admin" || user?.role === "admin_addl")) && (
                        <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => cancelRequest(r)}>Cancel</Button>
                      )}
                    </div>
                  </div>
                </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ---------------- Report ---------------- */}
        <TabsContent value="report" className="mt-6 space-y-3">
          <SampleReport buyers={buyers} />
        </TabsContent>

        {/* ---------------- Recycle Bin ---------------- */}
        {canDeleteSample && (
          <TabsContent value="recycle-bin" className="mt-6 space-y-3" data-testid="recycle-bin-tab-content">
            <Card className="p-4">
              <div className="flex flex-wrap items-center gap-3 justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2"><Trash2 className="h-4 w-4 text-brand" /> Recycle Bin</div>
                  <div className="text-xs text-slate-500">Deleted sample groups are kept here for 30 days, then automatically purged. You can restore or permanently drop them anytime.</div>
                </div>
                <Button size="sm" variant="outline" onClick={loadRecycleBin} data-testid="recycle-refresh-btn"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
              </div>
              {recycleLoading ? (
                <div className="text-sm text-slate-500">Loading…</div>
              ) : recycleBin.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="recycle-bin-empty">Recycle Bin is empty.</div>
              ) : (
                <div className="overflow-x-auto rounded-md border border-slate-200">
                  <table className="w-full text-sm" data-testid="recycle-bin-table">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2">Sample</th>
                        <th className="text-left px-3 py-2">BBS #</th>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-right px-3 py-2">Pcs</th>
                        <th className="text-left px-3 py-2">Deleted</th>
                        <th className="text-left px-3 py-2">By</th>
                        <th className="text-right px-3 py-2">Days left</th>
                        <th className="text-right px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recycleBin.map((r) => (
                        <tr key={r.sample_group_id} className="border-t border-slate-100" data-testid={`recycle-row-${r.sample_group_id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-800">{r.buyer_name || "—"}</div>
                            <div className="text-xs text-slate-500">{r.buyer_style || "—"}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{r.bbs_style || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {({
                              buyer_original: "Buyer's Original",
                              pp_gold_seal: "PP / Gold",
                              proto: "Proto",
                              size_set_silver_seal: "Size Set / Silver",
                            }[r.sample_type]) || r.sample_type}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.pieces_in_group}</td>
                          <td className="px-3 py-2 text-slate-600 text-xs">{(r.deleted_at || "").replace("T", " ").replace(/\.\d+.*$/, "")} UTC</td>
                          <td className="px-3 py-2 text-slate-600 text-xs">{r.deleted_by_name || r.deleted_by || "—"}</td>
                          <td className={`px-3 py-2 text-right tabular-nums text-xs font-semibold ${r.days_left <= 3 ? "text-amber-600" : "text-slate-600"}`}>{r.days_left}d</td>
                          <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                            <Button size="sm" variant="outline" onClick={() => restoreFromBin(r)} data-testid={`recycle-restore-${r.sample_group_id}`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                            </Button>
                            <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => purgeFromBin(r)} data-testid={`recycle-purge-${r.sample_group_id}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Purge
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Register dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="max-w-2xl" data-testid="register-sample-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-brand" />Register new sample</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Type *</label>
                <Select value={registerForm.sample_type} onValueChange={(v) => setRegisterForm({ ...registerForm, sample_type: v })}>
                  <SelectTrigger data-testid="register-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer_original">Buyer's Original</SelectItem>
                    <SelectItem value="pp_gold_seal">PP / Gold Seal</SelectItem>
                    <SelectItem value="proto">Proto</SelectItem>
                    <SelectItem value="size_set_silver_seal">Size Set / Silver Seal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Date of receipt / make *</label>
                <Input type="date" value={registerForm.date_of_receipt_or_make} onChange={(e) => setRegisterForm({ ...registerForm, date_of_receipt_or_make: e.target.value })} data-testid="register-date" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Buyer name *</label>
              {buyers.length ? (
                <Select value={registerForm.buyer_name} onValueChange={(v) => setRegisterForm({ ...registerForm, buyer_name: v })}>
                  <SelectTrigger data-testid="register-buyer-select"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                  <SelectContent>
                    {buyers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={registerForm.buyer_name} onChange={(e) => setRegisterForm({ ...registerForm, buyer_name: e.target.value })} data-testid="register-buyer-input" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Buyer style # *</label>
                <Input value={registerForm.buyer_style} onChange={(e) => setRegisterForm({ ...registerForm, buyer_style: e.target.value })} data-testid="register-buyer-style" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">BBS style # (optional)</label>
                <Input value={registerForm.bbs_style} onChange={(e) => setRegisterForm({ ...registerForm, bbs_style: e.target.value })} data-testid="register-bbs-style" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Number of pieces *</label>
              <Input
                type="number" min="1" max="50" value={registerForm.no_of_pcs}
                onChange={(e) => {
                  const n = Math.max(1, Math.min(50, Number(e.target.value) || 1));
                  const sizes = (registerForm.sizes || []).slice(0, n);
                  while (sizes.length < n) sizes.push("");
                  setRegisterForm({ ...registerForm, no_of_pcs: e.target.value, sizes });
                }}
                data-testid="register-no-of-pcs" />
              <div className="text-[10.5px] text-slate-400 mt-1">Each piece gets its own SEQ label (SEQ1…SEQn) and QR ID. Print sheet opens automatically after saving.</div>
            </div>
            {/* Per-piece size inputs */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Size per piece (optional)</label>
                {Number(registerForm.no_of_pcs) > 1 && (
                  <Button type="button" size="sm" variant="ghost" className="h-6 text-[10.5px] text-slate-500 hover:text-slate-800"
                    onClick={() => {
                      const first = String((registerForm.sizes || [])[0] || "").trim();
                      const n = Math.max(1, Math.min(50, Number(registerForm.no_of_pcs) || 1));
                      setRegisterForm({ ...registerForm, sizes: Array.from({ length: n }, () => first) });
                    }}
                    data-testid="register-size-copy-first"
                  >Copy SEQ1 → all</Button>
                )}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {Array.from({ length: Math.max(1, Math.min(50, Number(registerForm.no_of_pcs) || 1)) }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-0.5">
                    <label className="text-[9.5px] font-mono text-slate-500">SEQ{i + 1}</label>
                    <Input
                      value={(registerForm.sizes || [])[i] || ""}
                      onChange={(e) => {
                        const sizes = [...(registerForm.sizes || [])];
                        while (sizes.length <= i) sizes.push("");
                        sizes[i] = e.target.value;
                        setRegisterForm({ ...registerForm, sizes });
                      }}
                      placeholder="M / L / 32 …"
                      maxLength={30}
                      className="h-8 text-[13px]"
                      data-testid={`register-size-input-${i + 1}`}
                    />
                  </div>
                ))}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-1">Different sizes per SEQ or the same for all — both are fine. Prints on the sticker when filled.</div>
            </div>
            {/* Images (up to 5) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sample images (up to 10)</label>
                <span className="text-[10.5px] text-slate-400">{registerImages.length}/10 · ≤ 15 MB each · JPG / PNG / WEBP</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {registerImages.map((img, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200 bg-slate-50 group" data-testid={`register-image-${i}`}>
                    <img src={URL.createObjectURL(img)} alt={img.name} className="object-cover w-full h-full" />
                    <button
                      type="button"
                      className="absolute top-0 right-0 p-0.5 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setRegisterImages(registerImages.filter((_, j) => j !== i))}
                      data-testid={`register-image-remove-${i}`}
                    ><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {registerImages.length < 10 && (
                  <label className="w-20 h-20 rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-brand hover:text-brand cursor-pointer text-[10px]" data-testid="register-image-add">
                    <Plus className="h-4 w-4 mb-1" />Add
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (files.length === 0) return;
                        const remaining = 10 - registerImages.length;
                        if (remaining <= 0) { toast.error("Maximum 10 images per sample"); return; }
                        const queue = files.slice(0, remaining);
                        const oversize = queue.find((f) => f.size > 15 * 1024 * 1024);
                        if (oversize) { toast.error(`"${oversize.name}" exceeds 15 MB`); return; }
                        if (files.length > remaining) toast.warning(`Only ${remaining} of ${files.length} added — max 10 images per sample.`);
                        setRegisterImages([...registerImages, ...queue].slice(0, 10));
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-1">First image becomes the cover by default — you can change it later from the sample view. Click to enlarge.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRegisterOpen(false); setRegisterImages([]); }}>Cancel</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={submitRegister} disabled={registering} data-testid="register-submit">
              {registering ? "Saving…" : "Register & print tags"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate-group confirm dialog */}
      <Dialog open={!!dupCheck} onOpenChange={(o) => { if (!o) setDupCheck(null); }}>
        <DialogContent data-testid="dup-confirm-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-brand" /> Existing sample group found
            </DialogTitle>
          </DialogHeader>
          {dupCheck && (
            <div className="space-y-3 text-[13px]">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                A group already exists for this <span className="font-semibold">buyer · style · type · sample date</span> tuple.
              </div>
              <div className="rounded-md border border-slate-200 p-3 space-y-1.5">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12.5px]">
                  <div className="text-slate-500">Buyer</div><div className="font-semibold">{dupCheck.group.buyer_name}</div>
                  <div className="text-slate-500">Buyer Style</div><div className="font-mono">{dupCheck.group.buyer_style}</div>
                  <div className="text-slate-500">Sample Type</div><div className="capitalize">{(dupCheck.group.sample_type || "").replace("_"," ")}</div>
                  <div className="text-slate-500">Sample Date</div><div className="font-mono">{dupCheck.group.date_of_receipt_or_make}</div>
                  <div className="text-slate-500">Existing pcs</div>
                  <div className="font-semibold">
                    {dupCheck.group.count} <span className="font-mono text-slate-500">({dupCheck.group.seq_labels.join(", ")})</span>
                  </div>
                </div>
              </div>
              <div className="text-[12.5px] text-slate-600">
                Adding <span className="font-semibold">{dupCheck.form.no_of_pcs}</span> piece(s) will continue the SEQ counter — the new piece(s) become{" "}
                <span className="font-mono font-semibold text-brand">
                  SEQ{dupCheck.group.next_seq}
                  {dupCheck.form.no_of_pcs > 1 ? `…SEQ${dupCheck.group.next_seq + dupCheck.form.no_of_pcs - 1}` : ""}
                  /{dupCheck.group.count + dupCheck.form.no_of_pcs}
                </span>
                {" "}and existing pieces will be updated to the new /N denominator.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap sm:justify-between">
            <Button variant="outline" onClick={() => setDupCheck(null)} data-testid="dup-cancel">Cancel</Button>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => {
                  const f = dupCheck.form;
                  setDupCheck(null);
                  doRegister({ ...f });
                }}
                data-testid="dup-create-new"
              >
                Create separate group
              </Button>
              <Button
                className="bg-brand text-white hover:bg-brand-dark"
                disabled={registering}
                onClick={() => {
                  const { form, group } = dupCheck;
                  doRegister({ ...form, append_to_group_id: group.id });
                }}
                data-testid="dup-append"
              >
                {registering ? "Saving…" : `Append as SEQ${dupCheck?.group?.next_seq || ""}`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent data-testid="request-sample-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-brand" />Request sample</DialogTitle>
          </DialogHeader>
          {requestTarget && (
            <div className="text-[13px] text-slate-600 dark:text-slate-300">
              <div className="font-semibold">{requestTarget.buyer_name} · {requestTarget.buyer_style}</div>
              <div className="text-[12px] text-slate-500">{requestTarget.seq_label} · BBS# {requestTarget.bbs_style || "—"}</div>
            </div>
          )}
          <div className="space-y-3">
            <div className="text-[12px] text-slate-500 leading-relaxed">
              Stores will issue this sample to you. If it later needs to go to a client, use <b>Dispatch to client</b> from the sample detail once it's in your hands — you'll enter courier & waybill at that point.
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Expected return date *</label>
              <Input type="date" value={requestForm.expected_return_date} onChange={(e) => setRequestForm({ ...requestForm, expected_return_date: e.target.value })} data-testid="request-return-date" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Reason *</label>
              <Textarea rows={3} value={requestForm.reason} onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })} data-testid="request-reason" placeholder="Why do you need this sample?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestOpen(false)}>Cancel</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={submitRequest} data-testid="request-submit">Raise request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent data-testid="issue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpFromLine className="h-5 w-5 text-brand" />
              Issue sample
            </DialogTitle>
          </DialogHeader>
          {issueTarget && (
            <div className="text-[13px] text-slate-600 dark:text-slate-300">
              <div className="font-semibold">{issueTarget.sample?.buyer_name} · {issueTarget.sample?.buyer_style} · {issueTarget.sample?.seq_label}</div>
              <div className="text-[12px] text-slate-500">
                Requested by {issueTarget.requested_by_name} · Return by {fmtDate(issueTarget.expected_return_date)}
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Issued to (location / person) *</label>
              <Input value={issueForm.to_location} onChange={(e) => setIssueForm({ ...issueForm, to_location: e.target.value })} data-testid="issue-to-location" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Note (optional)</label>
              <Textarea rows={2} value={issueForm.note} onChange={(e) => setIssueForm({ ...issueForm, note: e.target.value })} data-testid="issue-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueOpen(false)}>Cancel</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={submitIssue} data-testid="issue-submit">Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch-to-client dialog (Merchandiser / Admin only) — reused for both single and bulk mode */}
      <Dialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <DialogContent data-testid="dispatch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-brand" />
              {dispatchMode === "bulk"
                ? `Dispatch ${bulkTargets.length} samples to client`
                : "Dispatch sample to client"}
            </DialogTitle>
          </DialogHeader>
          {dispatchMode === "single" && dispatchTarget && (
            <div className="text-[13px] text-slate-600 dark:text-slate-300">
              <div className="font-semibold">{dispatchTarget.sample?.buyer_name} · {dispatchTarget.sample?.buyer_style} · {dispatchTarget.sample?.seq_label}</div>
              <div className="text-[12px] text-slate-500">BBS# {dispatchTarget.sample?.bbs_style || "—"} · currently with {dispatchTarget.sample?.current_location || dispatchTarget.requested_by_name}</div>
            </div>
          )}
          {dispatchMode === "bulk" && bulkTargets.length > 0 && (
            <div className="text-[13px] text-slate-600 dark:text-slate-300" data-testid="bulk-dispatch-preview">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                {bulkTargets.length} samples · one shared courier + waybill · one consolidated email + WhatsApp
              </div>
              <ul className="max-h-40 overflow-auto text-[12.5px] pl-4 list-disc space-y-0.5 border rounded p-2 bg-slate-50/60">
                {bulkTargets.map((r) => (
                  <li key={r.id}>
                    <span className="font-semibold">{r.sample?.buyer_name} · {r.sample?.buyer_style}</span>
                    <span className="text-slate-500"> · {r.sample?.seq_label}</span>
                    {r.sample?.bbs_style && <span className="text-slate-400"> · BBS# {r.sample.bbs_style}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 block mb-1">Dispatch mode *</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setDispatchForm({ ...dispatchForm, client_mode: "return_back" })}
                  className={`text-[12.5px] px-2 py-1.5 rounded border ${dispatchForm.client_mode === "return_back" ? "border-brand bg-brand text-white font-semibold" : "border-slate-200 bg-white hover:border-brand/40"}`}
                  data-testid="dispatch-mode-return">
                  Return-back<br/><span className="text-[10.5px] opacity-70">Overdue alerts fire</span>
                </button>
                <button type="button" onClick={() => setDispatchForm({ ...dispatchForm, client_mode: "permanent" })}
                  className={`text-[12.5px] px-2 py-1.5 rounded border ${dispatchForm.client_mode === "permanent" ? "border-brand bg-brand text-white font-semibold" : "border-slate-200 bg-white hover:border-brand/40"}`}
                  data-testid="dispatch-mode-permanent">
                  Permanent<br/><span className="text-[10.5px] opacity-70">No return, no alerts</span>
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Client (buyer) *</label>
              {buyers.length ? (
                <Select value={dispatchForm.client_name} onValueChange={(v) => setDispatchForm({ ...dispatchForm, client_name: v })}>
                  <SelectTrigger data-testid="dispatch-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {buyers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={dispatchForm.client_name} onChange={(e) => setDispatchForm({ ...dispatchForm, client_name: e.target.value })} placeholder="e.g. Zara Milan" data-testid="dispatch-client-name" />
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Courier company *</label>
                <Input value={dispatchForm.courier} onChange={(e) => setDispatchForm({ ...dispatchForm, courier: e.target.value })} placeholder="DHL, FedEx, Bluedart…" data-testid="dispatch-courier" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Waybill / tracking # *</label>
                <Input value={dispatchForm.waybill} onChange={(e) => setDispatchForm({ ...dispatchForm, waybill: e.target.value })} data-testid="dispatch-waybill" />
              </div>
            </div>
            {dispatchForm.client_mode === "return_back" && (
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Expected return date *</label>
                <Input type="date" value={dispatchForm.expected_return_date} onChange={(e) => setDispatchForm({ ...dispatchForm, expected_return_date: e.target.value })} data-testid="dispatch-return-date" />
              </div>
            )}
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Note (optional)</label>
              <Textarea rows={2} value={dispatchForm.note} onChange={(e) => setDispatchForm({ ...dispatchForm, note: e.target.value })} data-testid="dispatch-note" />
            </div>
            <div className="text-[11px] text-slate-500 italic">
              Only Merchandiser + Admin roles can see Courier and Waybill after dispatch.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispatchOpen(false)}>Cancel</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={submitDispatch} data-testid="dispatch-submit">
              {dispatchMode === "bulk" ? `Dispatch ${bulkTargets.length} samples` : "Dispatch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sample detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl" data-testid="sample-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-brand" />Sample details</DialogTitle>
          </DialogHeader>
          {detailSample && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <img src={`${API_BASE}/samples/${detailSample.sample.id}/qr.png`} alt="QR" className="h-28 w-28 border rounded" />
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[15px] font-heading font-semibold">{detailSample.sample.buyer_name} · {detailSample.sample.buyer_style}</div>
                  <div className="text-[12.5px] text-slate-500">BBS# {detailSample.sample.bbs_style || "—"}</div>
                  <div className="text-[12.5px] mt-1">
                    <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${SAMPLE_TYPE_META[detailSample.sample.sample_type]?.color || ""}`}>{SAMPLE_TYPE_META[detailSample.sample.sample_type]?.label}</span>
                    <span className="ml-1.5 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_META[detailSample.sample.status]?.color || ''}">{STATUS_META[detailSample.sample.status]?.label}</span>
                  </div>
                  <div className="text-[11.5px] text-slate-500 mt-1">QR: <span className="font-mono">{detailSample.sample.qr_id}</span></div>
                  <div className="text-[11.5px] text-slate-500">Location: {detailSample.sample.current_location}</div>
                  <div className="text-[11.5px] text-slate-500">Registered: {fmtDate(detailSample.sample.registration_date)} · Received/made: {fmtDate(detailSample.sample.date_of_receipt_or_make)}</div>
                  <div className="text-[11.5px] text-slate-500">Piece {detailSample.sample.seq_label} of {detailSample.sample.no_of_pcs}{detailSample.sample.size ? <> · <span className="font-semibold text-slate-700">Size: {detailSample.sample.size}</span></> : null}</div>
                </div>
              </div>

              {/* Sample images (up to 5) — click cover to enlarge */}
              <SampleGroupImages
                groupId={detailSample.sample.sample_group_id}
                initial={detailSample.group_media || []}
                canEdit={canRegister}
              />

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Group ({detailSample.group.length} piece{detailSample.group.length !== 1 && "s"})</div>
                <div className="flex flex-wrap gap-1.5">
                  {detailSample.group.map((g) => (
                    <button key={g.id} onClick={() => openSampleDetail(g)} className={`text-[11.5px] px-2 py-1 rounded border ${g.id === detailSample.sample.id ? "bg-brand text-white border-brand" : "hover:bg-slate-50 dark:hover:bg-white/5"}`}>
                      {g.seq_label} · {STATUS_META[g.status]?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Transaction history</div>
                {detailSample.transactions.length === 0 ? (
                  <div className="text-[13px] text-slate-500">No transactions.</div>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {detailSample.transactions.map((t) => (
                      <div key={t.id} className="text-[12px] px-2 py-1.5 rounded border border-slate-100">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="capitalize text-[10.5px]">{t.action}</Badge>
                          <span className="text-slate-500">{t.from_location || "—"} → {t.to_location || "—"}</span>
                        </div>
                        {t.note && <div className="text-slate-600 mt-1">{t.note}</div>}
                        <div className="text-slate-400 text-[10.5px] mt-0.5">{fmtDateTime(t.at)} · {t.actor}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                <Button size="sm" variant="outline" onClick={() => printGroupTags(detailSample.sample)}><Printer className="h-3.5 w-3.5 mr-1" />Print group tags</Button>
                {canRequest && detailSample.sample.status === "in_stores" && (
                  <Button size="sm" variant="outline" onClick={() => { setDetailOpen(false); openRequest(detailSample.sample); }}><Send className="h-3.5 w-3.5 mr-1" />Request</Button>
                )}
                {canEditBbs && (
                  <Button size="sm" variant="ghost" onClick={() => updateBbsStyle(detailSample.sample)}>Edit BBS#</Button>
                )}
                {canFlipClient && (detailSample.sample.status === "at_client_return" || detailSample.sample.status === "at_client_permanent") && (
                  <Button size="sm" variant="outline" onClick={() => flipClientMode(detailSample.sample)} data-testid="detail-flip-client">
                    <Repeat className="h-3.5 w-3.5 mr-1" />Change to {detailSample.sample.status === "at_client_permanent" ? "Return-back" : "Permanent"}
                  </Button>
                )}
                {canMarkLost && detailSample.sample.status !== "lost" && (
                  <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => markLost(detailSample.sample)}><Ban className="h-3.5 w-3.5 mr-1" />Mark lost</Button>
                )}
                {canDeleteSample && (
                  <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50 hover:text-rose-800 ml-auto" onClick={() => deleteWholeSample(detailSample)} data-testid="detail-delete-sample">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />Delete sample
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR scanner (camera) */}
      <QRScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(qrId) => {
          setScannerOpen(false);
          toast.success(`Scanned ${qrId}`);
          nav(`/samples/scan/${qrId}`);
        }}
      />
      <BatchPrintDialog open={batchPrintOpen} onClose={() => setBatchPrintOpen(false)} buyers={buyers} />
    </div>
  );
}

/* -------------------- Batch QR print dialog -------------------- */

function BatchPrintDialog({ open, onClose, buyers = [] }) {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyerFilter, setBuyerFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({}); // {sample_id: true}
  const [startRow, setStartRow] = useState(1); // 1..6
  const [startCol, setStartCol] = useState(1); // 1..3
  const [repeat, setRepeat] = useState(1);
  const totalCols = 3, totalRows = 6;

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (buyerFilter !== "all") params.buyer_name = buyerFilter;
        if (typeFilter !== "all") params.sample_type = typeFilter;
        if (statusFilter !== "all") params.status = statusFilter;
        const { data } = await api.get("/samples", { params });
        setSamples(data || []);
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to load samples"); }
      setLoading(false);
    })();
  }, [open, buyerFilter, typeFilter, statusFilter]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return samples;
    return samples.filter((s) => (
      (s.buyer_style || "").toLowerCase().includes(qq) ||
      (s.bbs_style || "").toLowerCase().includes(qq) ||
      (s.buyer_name || "").toLowerCase().includes(qq) ||
      (s.qr_id || "").toLowerCase().includes(qq)
    ));
  }, [samples, q]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const startAt = ((Number(startRow) - 1) * totalCols) + (Number(startCol) - 1);
  const totalStickers = selectedIds.length * Math.max(1, Number(repeat));
  const stickersOnFirstPage = Math.max(0, Math.min(totalStickers, (totalCols * totalRows) - startAt));

  const toggleOne = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAllVisible = () => {
    const allOn = filtered.every((s) => selected[s.id]);
    setSelected((prev) => {
      const next = { ...prev };
      for (const s of filtered) next[s.id] = !allOn;
      return next;
    });
  };
  const clearSelection = () => setSelected({});

  const generate = () => {
    if (selectedIds.length === 0) { toast.error("Select at least one sample"); return; }
    const url = `${API_BASE}/samples/tags.pdf?ids=${selectedIds.join(",")}&start_at=${startAt}&repeat=${Math.max(1, Number(repeat))}`;
    window.open(url, "_blank", "noopener");
    toast.success(`Opening ${totalStickers} sticker(s) starting at row ${startRow}, col ${startCol}`);
  };

  const cellIdx = (r, c) => ((r - 1) * totalCols) + (c - 1);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="batch-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5 text-brand" /> Batch QR sticker print</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4 max-h-[70vh]">
          {/* Left — sample multi-select */}
          <div className="flex flex-col gap-2 min-h-0">
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Buyer</label>
                <Select value={buyerFilter} onValueChange={setBuyerFilter}>
                  <SelectTrigger className="h-8 text-[13px]" data-testid="batch-buyer-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All buyers</SelectItem>
                    {buyers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-8 text-[13px]" data-testid="batch-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="buyer_original">Buyer's Original</SelectItem>
                    <SelectItem value="pp_gold_seal">PP / Gold Seal</SelectItem>
                    <SelectItem value="proto">Proto</SelectItem>
                    <SelectItem value="size_set_silver_seal">Size Set / Silver Seal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-8 text-[13px]" data-testid="batch-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="in_stores">In Stores</SelectItem>
                    <SelectItem value="issued_out">Issued Out</SelectItem>
                    <SelectItem value="sent_to_client">Sent to Client</SelectItem>
                    <SelectItem value="scrapped">Scrapped</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Search</label>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Style / QR ID" className="h-8 text-[13px]" data-testid="batch-search" />
              </div>
            </div>

            <div className="flex items-center justify-between text-[11.5px] text-slate-600">
              <div>
                <span className="font-semibold">{filtered.length}</span> sample(s) match · <span className="font-semibold text-brand">{selectedIds.length}</span> selected
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={toggleAllVisible} data-testid="batch-toggle-all">
                  {filtered.length > 0 && filtered.every((s) => selected[s.id]) ? "Clear visible" : "Select all visible"}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={clearSelection} disabled={selectedIds.length === 0} data-testid="batch-clear">Clear all</Button>
              </div>
            </div>

            <div className="border border-slate-200 rounded-md overflow-y-auto max-h-[52vh]" data-testid="batch-samples-list">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    <th className="text-left py-1.5 px-2 w-8"></th>
                    <th className="text-left py-1.5 px-2">Type</th>
                    <th className="text-left py-1.5 px-2">Buyer</th>
                    <th className="text-left py-1.5 px-2">Buyer Style</th>
                    <th className="text-left py-1.5 px-2">BBS Style</th>
                    <th className="text-left py-1.5 px-2">SEQ</th>
                    <th className="text-left py-1.5 px-2">Size</th>
                    <th className="text-left py-1.5 px-2">QR ID</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="text-center py-4 text-slate-500">Loading…</td></tr>}
                  {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-slate-400 italic">No samples match.</td></tr>}
                  {!loading && filtered.map((s) => (
                    <tr key={s.id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selected[s.id] ? "bg-brand/5" : ""}`} onClick={() => toggleOne(s.id)}>
                      <td className="py-1.5 px-2"><Checkbox checked={!!selected[s.id]} onCheckedChange={() => toggleOne(s.id)} data-testid={`batch-select-${s.id}`} /></td>
                      <td className="py-1.5 px-2"><span className={`text-[10px] font-semibold px-1 py-0.5 rounded border ${SAMPLE_TYPE_META[s.sample_type]?.color || ""}`}>{SAMPLE_TYPE_META[s.sample_type]?.label || s.sample_type}</span></td>
                      <td className="py-1.5 px-2">{s.buyer_name}</td>
                      <td className="py-1.5 px-2 font-mono text-[11.5px]">{s.buyer_style}</td>
                      <td className="py-1.5 px-2 font-mono text-[11.5px] text-slate-500">{s.bbs_style || "—"}</td>
                      <td className="py-1.5 px-2 font-mono text-[11.5px]">{s.seq_label}/{s.no_of_pcs}</td>
                      <td className="py-1.5 px-2 font-mono text-[11.5px]">{s.size || "—"}</td>
                      <td className="py-1.5 px-2 font-mono text-[11.5px]">{s.qr_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right — start position picker + summary */}
          <div className="flex flex-col gap-3 p-3 bg-slate-50 rounded-md border border-slate-200 min-w-[240px]">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 mb-1">Start position on A4</div>
              <div className="text-[10.5px] text-slate-500 mb-2 leading-relaxed">Skip already-used slots on a partly-used sheet. First sticker prints at row {startRow}, col {startCol}.</div>
              {/* Visual 3×6 grid picker */}
              <div className="grid grid-cols-3 gap-1 max-w-[180px] mx-auto" data-testid="batch-grid-picker">
                {Array.from({ length: totalRows }).map((_, r) => (
                  Array.from({ length: totalCols }).map((_, c) => {
                    const row = r + 1, col = c + 1;
                    const idx = cellIdx(row, col);
                    const isStart = row === Number(startRow) && col === Number(startCol);
                    const isSkipped = idx < startAt;
                    const isFilled = idx >= startAt && (idx - startAt) < stickersOnFirstPage;
                    return (
                      <button
                        key={`${row}-${col}`}
                        type="button"
                        onClick={() => { setStartRow(row); setStartCol(col); }}
                        className={`aspect-[70/46] w-full rounded-sm border transition-colors flex items-center justify-center text-[9px] font-mono
                          ${isStart ? "border-brand bg-brand text-white ring-2 ring-brand/40 font-bold" :
                            isSkipped ? "border-slate-200 bg-slate-100 text-slate-400 line-through" :
                            isFilled ? "border-emerald-300 bg-emerald-50 text-emerald-700" :
                            "border-slate-200 bg-white text-slate-400 hover:border-brand"}`}
                        data-testid={`batch-cell-${row}-${col}`}
                        title={`Row ${row}, Col ${col}`}
                      >
                        {row},{col}
                      </button>
                    );
                  })
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Row</label>
                  <Input type="number" min={1} max={totalRows} value={startRow} onChange={(e) => setStartRow(Math.max(1, Math.min(totalRows, Number(e.target.value) || 1)))} className="h-7 text-[12px] font-mono text-center" data-testid="batch-start-row" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Col</label>
                  <Input type="number" min={1} max={totalCols} value={startCol} onChange={(e) => setStartCol(Math.max(1, Math.min(totalCols, Number(e.target.value) || 1)))} className="h-7 text-[12px] font-mono text-center" data-testid="batch-start-col" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Repeat each sticker</label>
              <Input type="number" min={1} max={200} value={repeat} onChange={(e) => setRepeat(Math.max(1, Math.min(200, Number(e.target.value) || 1)))} className="h-7 text-[12px] font-mono text-center" data-testid="batch-repeat" />
            </div>
            <div className="pt-2 border-t border-slate-200 text-[11px] font-mono text-slate-600 space-y-0.5">
              <div>Selected: <span className="text-slate-800 font-semibold">{selectedIds.length}</span></div>
              <div>Stickers: <span className="text-slate-800 font-semibold">{totalStickers}</span></div>
              <div>Pages: <span className="text-slate-800 font-semibold">{totalStickers === 0 ? 0 : Math.ceil((totalStickers - stickersOnFirstPage) / (totalCols * totalRows)) + (stickersOnFirstPage > 0 ? 1 : 0)}</span></div>
              <div>Skipped on 1st page: <span className="text-slate-500">{startAt}</span></div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
          <Button variant="outline" onClick={onClose} data-testid="batch-print-cancel">Cancel</Button>
          <Button className="bg-brand text-white hover:bg-brand-dark" onClick={generate} disabled={selectedIds.length === 0} data-testid="batch-print-generate">
            <Printer className="h-4 w-4 mr-1.5" /> Generate PDF ({totalStickers})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------- Sample Report ----------------
function SampleReport({ buyers }) {
  const [filters, setFilters] = useState({
    sample_type: "all",
    status_filter: "all",
    buyer: "",
    bbs_style: "",
    current_location: "",
    date_from: "",
    date_to: "",
    requester: "",
    q: "",
  });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  const sampleColumns = useMemo(() => ({
    reg:         { get: (r) => r.sample?.registration_date || "", type: "date" },
    type:        { get: (r) => SAMPLE_TYPE_META[r.sample?.sample_type]?.label || r.sample?.sample_type || "", type: "text" },
    buyer:       { get: (r) => r.sample?.buyer_name || "", type: "text" },
    buyer_style: { get: (r) => r.sample?.buyer_style || "", type: "text" },
    bbs_style:   { get: (r) => r.sample?.bbs_style || "", type: "text" },
    seq:         { get: (r) => r.sample?.seq_label || "", type: "text" },
    status:      { get: (r) => r.sample?.status || "", type: "text" },
    location:    { get: (r) => r.sample?.current_location || "", type: "text" },
    requester:   { get: (r) => r.current_request?.requested_by_name || "", type: "text" },
    return_by:   { get: (r) => r.current_request?.expected_return_date || "", type: "date" },
  }), []);
  const { sortedRows: sampleRows, sortKey: sSortKey, sortDir: sSortDir, cycleSort: sCycle } = useSortableTable(rows, sampleColumns, { defaultKey: "reg", defaultDir: "desc", storageKey: "sort:opsiq:samples" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/samples/report", { params });
      setRows(data.rows || []);
      setTotals(data.totals || null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load report"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportExcel = () => {
    if (!rows.length) { toast.error("No rows to export"); return; }
    const data = rows.map((r, i) => ({
      "#": i + 1,
      "Reg. Date": r.sample.registration_date,
      "Type": SAMPLE_TYPE_META[r.sample.sample_type]?.label || r.sample.sample_type,
      "Buyer": r.sample.buyer_name,
      "Buyer Style": r.sample.buyer_style,
      "BBS Style": r.sample.bbs_style || "",
      "SEQ": `${r.sample.seq_label} / ${r.sample.no_of_pcs}`,
      "Size": r.sample.size || "",
      "QR ID": r.sample.qr_id,
      "Status": (STATUS_META[r.sample.status] || {}).label || r.sample.status,
      "Current Location": r.sample.current_location,
      "Requester": r.current_request?.requested_by_name || "",
      "Return by": r.current_request?.expected_return_date || "",
      "Client": r.current_request?.client_name || "",
      "Courier": r.current_request?.courier || "",
      "Waybill": r.current_request?.waybill || "",
      "Overdue": r.is_overdue ? "YES" : "",
      "Movement": (r.movement || []).map((m) => `${(m.at || "").slice(0, 16).replace("T", " ")} · ${m.action} · ${m.from || "—"} → ${m.to || "—"} · ${m.actor}`).join(" | "),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample Report");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `sample-report-${stamp}.xlsx`);
  };

  const exportPdf = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.append(k, v); });
    window.open(`${API_BASE}/samples/report.pdf?${params.toString()}`, "_blank", "noopener");
  };

  const clearFilters = () => {
    setFilters({ sample_type: "all", status_filter: "all", buyer: "", bbs_style: "", current_location: "", date_from: "", date_to: "", requester: "", q: "" });
  };

  return (
    <div className="space-y-3" data-testid="sample-report-tab">
      <Card className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Type</label>
            <Select value={filters.sample_type} onValueChange={(v) => setFilters({ ...filters, sample_type: v })}>
              <SelectTrigger data-testid="report-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="buyer_original">Buyer's Original</SelectItem>
                <SelectItem value="pp_gold_seal">PP / Gold Seal</SelectItem>
                <SelectItem value="proto">Proto</SelectItem>
                <SelectItem value="size_set_silver_seal">Size Set / Silver Seal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Status</label>
            <Select value={filters.status_filter} onValueChange={(v) => setFilters({ ...filters, status_filter: v })}>
              <SelectTrigger data-testid="report-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="in_stores">In Stores</SelectItem>
                <SelectItem value="issued">Issued (internal)</SelectItem>
                <SelectItem value="at_client">At Client (any)</SelectItem>
                <SelectItem value="at_client_return">At Client · Return-back</SelectItem>
                <SelectItem value="at_client_permanent">At Client · Permanent</SelectItem>
                <SelectItem value="out">Out (any)</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Buyer</label>
            {buyers && buyers.length ? (
              <Select value={filters.buyer || "__any__"} onValueChange={(v) => setFilters({ ...filters, buyer: v === "__any__" ? "" : v })}>
                <SelectTrigger data-testid="report-buyer"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any buyer</SelectItem>
                  {buyers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={filters.buyer} onChange={(e) => setFilters({ ...filters, buyer: e.target.value })} placeholder="Buyer name" data-testid="report-buyer" />
            )}
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">BBS Style</label>
            <Input value={filters.bbs_style} onChange={(e) => setFilters({ ...filters, bbs_style: e.target.value })} placeholder="BBS-…" data-testid="report-bbs" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Location</label>
            <Input value={filters.current_location} onChange={(e) => setFilters({ ...filters, current_location: e.target.value })} placeholder="Line 3, DHL…" data-testid="report-location" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Requester</label>
            <Input value={filters.requester} onChange={(e) => setFilters({ ...filters, requester: e.target.value })} placeholder="Name or email" data-testid="report-requester" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Registered from</label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="report-date-from" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Registered to</label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="report-date-to" />
          </div>
          <div className="col-span-2 md:col-span-2 lg:col-span-2">
            <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Search</label>
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Buyer, style, BBS#, QR…" data-testid="report-q" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3 justify-end">
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="report-clear">Clear</Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="report-apply">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Apply
          </Button>
          <Button size="sm" variant="outline" onClick={exportExcel} data-testid="report-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button size="sm" className="bg-brand text-white hover:bg-brand-dark" onClick={exportPdf} data-testid="report-export-pdf"><Printer className="h-4 w-4 mr-1.5" />PDF</Button>
        </div>
      </Card>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {[
            { k: "count", label: "Total" },
            { k: "in_stores", label: "In Stores" },
            { k: "issued", label: "Issued" },
            { k: "at_client_return", label: "At Client (RB)" },
            { k: "at_client_permanent", label: "At Client (Perm)" },
            { k: "overdue", label: "Overdue" },
          ].map((t) => (
            <div key={t.k} className="rounded border border-slate-200 bg-white px-3 py-2">
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">{t.label}</div>
              <div className="text-lg font-heading font-bold">{totals[t.k] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="opsiq-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}>#</th>
                <SortableTh label="Reg." colKey="reg" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="Type" colKey="type" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="Buyer" colKey="buyer" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="Buyer Style" colKey="buyer_style" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="BBS Style" colKey="bbs_style" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="SEQ" colKey="seq" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="Status" colKey="status" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="Location" colKey="location" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="Requester" colKey="requester" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <SortableTh label="Return by" colKey="return_by" sortKey={sSortKey} sortDir={sSortDir} onClick={sCycle} />
                <th>Movement</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-6 text-slate-500">Loading…</td></tr>
              ) : sampleRows.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-6 text-slate-500">No samples match your filters.</td></tr>
              ) : sampleRows.map((r, i) => {
                const s = r.sample;
                const req = r.current_request;
                return (
                  <tr key={s.id} className={r.is_overdue ? "bg-rose-50/40" : ""} data-testid={`report-row-${s.id}`}>
                    <td className="font-mono text-[11px] text-slate-400">{i + 1}</td>
                    <td className="text-[11.5px]">{fmtDate(s.registration_date)}</td>
                    <td><span className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${SAMPLE_TYPE_META[s.sample_type]?.color || ""}`}>{SAMPLE_TYPE_META[s.sample_type]?.label || s.sample_type}</span></td>
                    <td className="text-[12.5px]">{s.buyer_name}</td>
                    <td className="text-[12.5px] font-mono">{s.buyer_style}</td>
                    <td className="text-[12.5px] font-mono">{s.bbs_style || "—"}</td>
                    <td className="text-[11.5px] font-mono">{s.seq_label}/{s.no_of_pcs}</td>
                    <td>
                      <span className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${STATUS_META[s.status]?.color || ""}`}>{STATUS_META[s.status]?.label || s.status}</span>
                      {r.is_overdue && <div className="mt-0.5"><span className="text-[10.5px] font-semibold text-rose-700">OVERDUE</span></div>}
                    </td>
                    <td className="text-[12px]">{s.current_location}</td>
                    <td className="text-[12px]">{req?.requested_by_name || "—"}{req?.client_name ? <div className="text-[10.5px] text-slate-500 truncate">Client: {req.client_name}</div> : null}</td>
                    <td className="text-[11.5px] font-mono">{req?.expected_return_date || (req?.client_mode === "permanent" ? <span className="text-fuchsia-700">Permanent</span> : "—")}</td>
                    <td className="text-[10.5px] text-slate-600 max-w-md">
                      {(r.movement || []).slice(-4).map((m, idx) => (
                        <div key={idx} className="truncate">
                          <span className="text-slate-400">{(m.at || "").slice(0, 16).replace("T", " ")}</span> · <b>{m.action}</b> · {m.from || "—"} → {m.to || "—"}
                        </div>
                      ))}
                      {(r.movement || []).length > 4 && <div className="text-slate-400">+ {(r.movement || []).length - 4} more…</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

